import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.filter((a) => !a.featured);

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '91自拍论坛 - 摄影爱好者自拍交流社区',
    description: '专为摄影爱好者打造的自拍技巧交流平台，汇聚海量自拍教程、构图技巧、光线运用与后期修图经验分享。',
    url: 'https://www.91selfie.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '91自拍论坛',
      url: 'https://www.91selfie.com/',
    },
    about: {
      '@type': 'Organization',
      name: '91自拍论坛',
      url: 'https://www.91selfie.com/',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20photography%20studio%20scene%20with%20a%20modern%20mirrorless%20camera%20on%20a%20minimalist%20wooden%20surface%2C%20soft%20golden%20hour%20light%20streaming%20through%20large%20windows%2C%20elegant%20cream%20and%20beige%20color%20palette%2C%20photography%20prints%20on%20the%20wall%2C%20warm%20and%20inviting%20creative%20atmosphere%2C%20lifestyle%20photography%20aesthetic&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
              alt="91自拍论坛 - 摄影爱好者自拍交流社区"
              title="91自拍论坛 - 摄影爱好者自拍交流社区"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>

            <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-3xl mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 mb-6 leading-tight">
                用镜头记录最美的自己
              </h1>
              <p className="text-base md:text-lg text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
                91自拍论坛汇聚海量自拍技巧教程，从构图到光线，从拍摄到修图，与万千摄影达人共同探索自拍艺术
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/composition"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-300"
                >
                  开始学习
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/15 hover:bg-background-50/25 text-background-50 backdrop-blur-sm rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-300"
                >
                  了解更多
                </Link>
              </div>
            </div>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
              <div className="w-8 h-12 flex items-center justify-center">
                <i className="ri-mouse-line text-background-50/60 text-xl animate-bounce"></i>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  探索自拍技巧
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
                  五大核心板块，系统学习自拍摄影的每一个环节
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group relative bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 overflow-hidden transition-all duration-300 cursor-pointer p-5"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300 mx-auto">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 text-center mb-2 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 text-center leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                    <p className="text-xs text-primary-500 text-center mt-3 font-medium">
                      {cat.articleCount} 篇教程
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                    精选推荐
                  </h2>
                  <p className="text-sm text-foreground-500">编辑精心挑选的高质量教程</p>
                </div>
                <Link
                  to="/category/composition"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                    最新教程
                  </h2>
                  <p className="text-sm text-foreground-500">持续更新的自拍技巧内容</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>

              <div className="text-center mt-10">
                <Link
                  to="/category/editing"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-300"
                >
                  浏览更多教程
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 md:py-16 bg-primary-500">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { number: '500+', label: '精选教程' },
                  { number: '50,000+', label: '社区成员' },
                  { number: '120+', label: '签约达人' },
                  { number: '99.2%', label: '好评率' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="font-heading text-3xl md:text-4xl text-background-50 mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-background-50/70">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">
                  关于91自拍论坛，你想知道的都在这里
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="font-medium text-sm md:text-base text-foreground-800 pr-4">
                        {faq.question}
                      </span>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </div>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="relative py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Creative%20photography%20community%20gathering%20with%20warm%20atmosphere%2C%20people%20sharing%20and%20discussing%20photos%20in%20a%20bright%20modern%20co-working%20space%2C%20cream%20and%20beige%20interior%2C%20large%20windows%20with%20natural%20light%2C%20artistic%20lifestyle%20photography&width=1600&height=600&seq=cta-bg-2026&orientation=landscape"
              alt="加入91自拍论坛社区"
              title="加入91自拍论坛社区"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="relative z-10 text-center px-4 md:px-6 max-w-xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">
                加入91自拍论坛，开启你的摄影之旅
              </h2>
              <p className="text-sm md:text-base text-background-50/80 mb-8 leading-relaxed">
                与万千摄影爱好者一起，学习自拍技巧，分享创作心得，让你的每一张自拍都成为艺术品
              </p>
              <Link
                to="/about"
                className="inline-block px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-300"
              >
                立即加入
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}